import { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';

import countryData from '../config/dropdowns/country.json';
import industryData from '../config/dropdowns/industry.json';
import {
  CAMPAIGN_DEMAND_SOURCES,
  CAMPAIGN_DEMAND_SOURCES_LIST,
  CAMPAIGN_STATUSES,
  CAMPAIGN_STATUSES_LIST,
} from '../constants/campaign';
import { CAMPAIGN_ID_CONFIG, parseCommaSeparatedIds } from '../utils/campaignIds';
import { TIMEZONES, UTC_TIMEZONE_VALUE } from '../constants/tz';
import { MAX_RESPONSES_PER_GROUP, DEFAULT_RESPONSES_PER_GROUP } from '../config/global';
import { createCampaign, updateCampaign } from '../service/brc';
import { AlertDismissable } from './Alerts';

const cpmBidValidator = (v) => v > 0 && v < 10;

/**
 * Converts ISO-8601 date-time representation to date-only
 * @example dateTimeToDateString("2025-09-08T15:15:00.000") // -> "2025-09-08"
 * @param {string | undefined} dateTimeString
 */
const dateTimeToDateString = (dateTimeString) => dateTimeString?.split('T')[0];

const selectAutomaticCampaignFormData = (data = {}) => ({
  status: data.status ?? CAMPAIGN_STATUSES.ACTIVE,
  targetNumberOfResponses: data.targetNumberOfResponses ?? DEFAULT_RESPONSES_PER_GROUP,
  targetNumberOfSurveyImpressions: data.targetNumberOfSurveyImpressions,
  campaignDemandSource: data.campaignDemandSource ?? CAMPAIGN_DEMAND_SOURCES.PORTAL,
  campaignIds: CAMPAIGN_ID_CONFIG[data.campaignDemandSource] ? data.campaignIds : [],
  dealIds: data.campaignDemandSource === CAMPAIGN_DEMAND_SOURCES.PMP ? data.dealIds : [],
  controlCpmBid: data.controlCpmBid ?? 1,
  exposedCpmBid: data.exposedCpmBid ?? 1,
});

const selectCommonCampaignFormData = (data = {}) => {
  return {
    id: data.id,
    opportunityId: data.opportunityId,
    campaignName: data.campaignName,
    campaignStart: dateTimeToDateString(data.campaignStart),
    campaignEnd: dateTimeToDateString(data.campaignEnd),
    campaignDetails: data.campaignDetails,
    country: data.country ?? '',
    industry: data.industry ?? '',
    automatic: data.automatic ?? false,
    tzOffset: data.tzOffset ?? UTC_TIMEZONE_VALUE,
  };
};

const CampaignForm = (props) => {
  const [isLoading, setLoading] = useState(false);
  const [showAlert, setAlert] = useState(false);
  const [isError, setError] = useState(false);
  const [message, setMessage] = useState('Unknown Error!');

  const {
    register,
    watch,
    setValue,
    getValues,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    values: {
      ...selectAutomaticCampaignFormData(props.campaign),
      ...selectCommonCampaignFormData(props.campaign),
    },
  });

  const isInvalid = (name) => !!(errors && errors[name]);

  const country = watch('country');
  const manual = !watch('automatic');
  const campaignEnd = watch('campaignEnd');
  const campaignStart = watch('campaignStart');
  const campaignDemandSource = watch('campaignDemandSource');
  const isPmp = campaignDemandSource === CAMPAIGN_DEMAND_SOURCES.PMP;
  const campaignIdConfig = CAMPAIGN_ID_CONFIG[campaignDemandSource];
  if (!campaignIdConfig && !isPmp && !manual) {
    throw new Error(`No campaign ID config for demand source: ${campaignDemandSource}`);
  }

  useEffect(() => {
    if (!country) {
      return;
    }

    const match = country.match(/\(([^)]+)\)/);
    const newCountryCode = match ? match[1] : '';
    const parts = getValues().campaignName?.split('//') ?? [];
    const existingTextAfterPrefix = parts.length > 1 ? parts.slice(1).join('//').trimStart() : '';

    setValue('campaignName', `${newCountryCode} // ${existingTextAfterPrefix}`);
  }, [country, getValues, setValue]);

  const dismissAlert = () => setAlert(false);

  /**
   * @param {Parameters<Parameters<typeof handleSubmit>[0]>[0]} formData
   * @param {Parameters<Parameters<typeof handleSubmit>[0]>[1]} event
   */
  const submit = async (formData, event) => {
    const requestData = {
      ...selectCommonCampaignFormData(formData),
      ...(formData.automatic ? selectAutomaticCampaignFormData(formData) : {}),
    };

    setLoading(true);

    let response = {};
    if (props.isEdit) {
      response = await updateCampaign(requestData, formData.id);
    } else {
      response = await createCampaign(requestData);
    }

    if (response && response.data) {
      if (response.data._status === true) {
        setError(false);
        setMessage(response.data._message);
        setAlert(true);
        handleClose();
        window.location.reload();
      } else {
        setError(true);
        setMessage(response.data._message);
        setAlert(true);
      }
      setLoading(false);
    }
  };

  const handleClose = () => {
    dismissAlert();
    props.setShow(false);
  };

  return (
    <Modal
      show={props.show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton closeLabel="" style={{ borderRadius: 5 }}>
        {props.isEdit && <Modal.Title>Edit Campaign</Modal.Title>}
        {!props.isEdit && <Modal.Title>Create Campaign</Modal.Title>}
      </Modal.Header>
      <Modal.Body>
        <AlertDismissable
          dismiss={dismissAlert}
          show={showAlert}
          message={message}
          type={isError ? 'error' : 'success'}
        />
        <Form onSubmit={handleSubmit(submit)}>
          <Form.Group className="mb-3">
            <Controller
              name="automatic"
              control={control}
              render={({ field }) => {
                return (
                  <>
                    <Form.Check
                      id="setup-manual"
                      type="radio"
                      value="false"
                      disabled={props.isEdit}
                      label="Setup manually on Ops Portal"
                      checked={!field.value}
                      name={field.name}
                      onBlur={field.onBlur}
                      onChange={() => field.onChange(false)}
                    />
                    <Form.Check
                      id="setup-automatic"
                      type="radio"
                      value="true"
                      disabled={props.isEdit}
                      label="Setup automatically"
                      checked={field.value}
                      name={field.name}
                      onBlur={field.onBlur}
                      onChange={() => field.onChange(true)}
                    />
                  </>
                );
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3" hidden={manual} controlId="status">
            <Form.Label>Status</Form.Label>
            <Form.Select
              isInvalid={isInvalid('status')}
              {...register('status', { required: true, disabled: manual })}
            >
              {CAMPAIGN_STATUSES_LIST.map(({ value, label }) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="industry">
            <Form.Label>Industry</Form.Label>
            <Form.Select
              isInvalid={isInvalid('industry')}
              {...register('industry', { required: true })}
              placeholder=""
            >
              <option value="">Please Choose Industry</option>
              {industryData.map((industry, index) => (
                <option value={industry} key={index}>
                  {industry}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="country">
            <Form.Label>Country</Form.Label>
            <Form.Select
              isInvalid={isInvalid('country')}
              {...register('country', { required: true })}
            >
              <option value="">Please Choose Country</option>
              {countryData.map((country, index) => (
                <option value={country} key={index}>
                  {country}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="campaignName">
            <Form.Label>Campaign Name</Form.Label>
            <Form.Control
              isInvalid={isInvalid('campaignName')}
              type="text"
              {...register('campaignName', { required: true })}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="opportunityId">
            <Form.Label>Opportunity ID</Form.Label>
            <Form.Control
              isInvalid={isInvalid('opportunityId')}
              type="text"
              {...register('opportunityId', { required: true })}
            />
          </Form.Group>

          <Form.Group className="mb-3" hidden={manual}>
            <Form.Label htmlFor={isPmp ? 'dealIds' : 'campaignIds'}>
              Target Campaign/Deal IDs (comma separated)
            </Form.Label>
            <Form.Select
              isInvalid={isInvalid('campaignDemandSource')}
              className="mb-3"
              {...register('campaignDemandSource', {
                required: true,
                disabled: manual,
              })}
            >
              {CAMPAIGN_DEMAND_SOURCES_LIST.map(({ value, label }) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
            {isPmp ? (
              <>
                <Form.Control
                  id="dealIds"
                  as="textarea"
                  rows={3}
                  {...register('dealIds', {
                    disabled: manual,
                    setValueAs: parseCommaSeparatedIds,
                    validate: (v) =>
                      manual ||
                      (Array.isArray(v) && v.length > 0 && v.every((id) => id.length > 0)) ||
                      'Target Deal IDs are required.',
                  })}
                  isInvalid={isInvalid('dealIds')}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.dealIds?.message}
                </Form.Control.Feedback>
              </>
            ) : (
              <>
                <Form.Control
                  id="campaignIds"
                  as="textarea"
                  rows={3}
                  {...register('campaignIds', {
                    disabled: manual || isPmp,
                    setValueAs: campaignIdConfig?.parse,
                    validate: (v) =>
                      manual ||
                      isPmp ||
                      (Array.isArray(v) && v.length > 0 && v.every(campaignIdConfig?.isValid)) ||
                      campaignIdConfig?.error,
                  })}
                  isInvalid={isInvalid('campaignIds')}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.campaignIds?.message}
                </Form.Control.Feedback>
              </>
            )}
          </Form.Group>
          { /* TODO: BLS-148 Hiding just from the UI for now,
            might be removed entirely if BB pacing goes well */}
          {/* <Form.Group
            className="mb-3"
            controlId="targetNumberOfSurveyImpressions"
            hidden={manual}
          >
            <Form.Label>Total Impressions Per Group</Form.Label>
            <Form.Control
              type="number"
              isInvalid={isInvalid('targetNumberOfSurveyImpressions')}
              {...register('targetNumberOfSurveyImpressions', {
                disabled: manual,
                min: 1,
                required: true,
                valueAsNumber: true,
              })}
            />
          </Form.Group> */}

          <Form.Group
            className="mb-3"
            controlId="targetNumberOfResponses"
            hidden={manual}
          >
            <Form.Label>Total Responses Per Group</Form.Label>
            <Form.Control
              type="number"
              isInvalid={isInvalid('targetNumberOfResponses')}
              {...register('targetNumberOfResponses', {
                disabled: manual,
                min: { value: 1, message: 'Number of survey responses per group must be >= 1.' },
                max: {
                  value: MAX_RESPONSES_PER_GROUP,
                  message: `Number of survey responses per group must be <= ${MAX_RESPONSES_PER_GROUP.toLocaleString()}. Default value which works best is ${DEFAULT_RESPONSES_PER_GROUP}.`,
                },
                required: true,
                valueAsNumber: true,
              })}
            />
            <Form.Control.Feedback type="invalid">
              {errors.targetNumberOfResponses?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Row className="mb-3" hidden={manual}>
            <Form.Group as={Col} controlId="exposedCpmBid">
              <Form.Label>Exposed Bid Rate ($ CPM)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                isInvalid={isInvalid('exposedCpmBid')}
                {...register('exposedCpmBid', {
                  disabled: manual,
                  validate: cpmBidValidator,
                  required: true,
                  valueAsNumber: true,
                })}
              />
            </Form.Group>

            <Form.Group as={Col} controlId="controlCpmBid">
              <Form.Label>Control Bid Rate ($ CPM)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                isInvalid={isInvalid('controlCpmBid')}
                {...register('controlCpmBid', {
                  disabled: manual,
                  validate: cpmBidValidator,
                  required: true,
                  valueAsNumber: true,
                })}
              />
            </Form.Group>
          </Row>

          <Form.Group className="mb-3" controlId="campaignStart">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              max={campaignEnd}
              isInvalid={isInvalid('campaignStart')}
              {...register('campaignStart', {
                required: true,
              })}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="campaignEnd">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              min={campaignStart}
              isInvalid={isInvalid('campaignEnd')}
              {...register('campaignEnd', {
                required: true,
              })}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="tzOffset">
            <Form.Label>Time zone</Form.Label>
            <Form.Select
              isInvalid={isInvalid('tzOffset')}
              {...register('tzOffset', { required: true })}
            >
              {TIMEZONES.map(({ value, label }) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="campaignDetails">
            <Form.Label>Details</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              isInvalid={isInvalid('campaignDetails')}
              {...register('campaignDetails', { required: true })}
            />
            <Form.Control.Feedback type="invalid">
              {errors.campaignDetails?.message || 'This field is required'}
            </Form.Control.Feedback>
          </Form.Group>

          <Button variant="primary" type="submit">
            {props.isEdit ? 'Update' : 'Create'}
          </Button>

        </Form>
      </Modal.Body>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 999,
            backgroundColor: '#000000c4',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      )}
    </Modal>
  );
};

export default CampaignForm;
